import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import ConfirmDelete from "@/components/ConfirmDelete";
import DataTable from "@/components/DataTable";
import { Plus, KeyRound, Trash2, Shield, User as UserIcon, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

const emptyCreate = { email: "", password: "", name: "", role: "user" };

const fmtDateTime = (v) => {
  if (!v) return "Mai";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} · ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function Utenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(() => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { id: "nome", label: "Nome", width: 200, sortable: true, sortValue: (u) => u.name || "",
      cell: (u) => <span className="font-medium text-slate-800">{u.name}{u.id === me?.id && <span className="ml-2 text-[10px] font-semibold text-blue-600 uppercase">(tu)</span>}</span> },
    { id: "email", label: "Email", width: 230, sortable: true, sortValue: (u) => u.email || "",
      cell: (u) => <span className="text-slate-600">{u.email}</span> },
    { id: "ruolo", label: "Ruolo", width: 180, sortable: true, sortValue: (u) => u.role || "",
      cell: (u) => u.id === me?.id ? (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${u.role === "admin" ? "bg-blue-50 text-blue-800" : "bg-slate-100 text-slate-600"}`}>
          {u.role === "admin" ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
          {u.role === "admin" ? "Amministratore" : "Utente"}
        </span>) : (
        <Select value={u.role || "user"} onValueChange={(v) => changeRole(u, v)}>
          <SelectTrigger data-testid={`user-role-change-${u.id}`} className={`h-8 w-40 text-xs font-medium ${u.role === "admin" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-50 text-slate-700"}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="user" data-testid={`user-role-option-user-${u.id}`}>Utente</SelectItem>
            <SelectItem value="admin" data-testid={`user-role-option-admin-${u.id}`}>Amministratore</SelectItem>
          </SelectContent>
        </Select>) },
    { id: "stato", label: "Stato", width: 130, sortable: true, sortValue: (u) => (u.disabled ? 1 : 0),
      cell: (u) => (
        <span data-testid={`user-status-${u.id}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${u.disabled ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${u.disabled ? "bg-rose-500" : "bg-emerald-500"}`} />
          {u.disabled ? "Disattivato" : "Attivo"}
        </span>) },
    { id: "ultimo", label: "Ultimo accesso", width: 170, sortable: true, sortValue: (u) => u.last_login || "",
      cell: (u) => <span className="text-slate-500 tabular-nums text-xs">{fmtDateTime(u.last_login)}</span> },
    { id: "creato", label: "Creato il", width: 130, sortable: true, sortValue: (u) => u.created_at || "",
      cell: (u) => <span className="text-slate-500 tabular-nums">{formatDate(u.created_at)}</span> },
    { id: "azioni", label: "Azioni", width: 150, align: "right", sortable: false,
      cell: (u) => (
        <div className="flex items-center justify-end gap-0.5">
          {u.id !== me?.id && (
            <Button variant="ghost" size="icon" data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)}
              className={`h-8 w-8 ${u.disabled ? "text-slate-400 hover:text-emerald-600" : "text-slate-400 hover:text-amber-600"}`}
              title={u.disabled ? "Attiva account" : "Disattiva account"}>
              {u.disabled ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
            </Button>
          )}
          <Button variant="ghost" size="icon" data-testid={`reset-pw-${u.id}`} onClick={() => openReset(u)} className="h-8 w-8 text-slate-400 hover:text-blue-700" title="Reset password">
            <KeyRound className="h-4 w-4" />
          </Button>
          {u.id !== me?.id && (
            <ConfirmDelete title="Eliminare utente?" description={`L'utente ${u.email} non potrà più accedere.`} actionTestid={`confirm-delete-user-${u.id}`} onConfirm={() => removeUser(u.id)}>
              <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`} className="h-8 w-8 text-slate-400 hover:text-rose-600" title="Elimina">
                <Trash2 className="h-4 w-4" />
              </Button>
            </ConfirmDelete>
          )}
        </div>) },
  ];

  const createUser = async () => {
    if (!form.email.trim() || form.password.length < 6) return toast.error("Email valida e password (min 6 caratteri) richieste");
    try {
      await api.post("/auth/users", form);
      toast.success("Utente creato");
      setCreateOpen(false);
      setForm(emptyCreate);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openReset = (u) => { setPwTarget(u); setNewPw(""); setPwOpen(true); };
  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("La password deve avere almeno 6 caratteri");
    try {
      await api.post(`/auth/users/${pwTarget.id}/reset-password`, { password: newPw });
      toast.success(`Password aggiornata per ${pwTarget.email}`);
      setPwOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.post(`/auth/users/${u.id}/role`, { role });
      toast.success(`${u.email} ora è ${role === "admin" ? "Amministratore" : "Utente"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.post(`/auth/users/${u.id}/toggle-active`);
      toast.success(u.disabled ? `Account ${u.email} attivato` : `Account ${u.email} disattivato`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      

      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8">
        <PageHeader title="Utenti" subtitle="Gestione degli accessi · solo amministratore">
        <Button data-testid="add-user-button" onClick={() => { setForm(emptyCreate); setCreateOpen(true); }}
          className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] transition-transform">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo utente
        </Button>
      </PageHeader>
        <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden" data-testid="users-table">
          <DataTable tableKey="utenti" columns={columns} rows={users} rowKey={(u) => u.id}
            rowTestId={(u) => `user-row-${u.id}`} emptyText="Nessun utente."
            containerClassName="max-h-[calc(100vh-220px)]" initialSort={{ key: "nome", dir: "asc" }} />
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo utente</DialogTitle>
            <DialogDescription>Crea un accesso impostando email e password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-slate-700 text-sm">Nome</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Es. Mario Rossi" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="nome@azienda.it" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Password</Label>
              <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" placeholder="Almeno 6 caratteri" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Ruolo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utente</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button data-testid="save-user-button" onClick={createUser} className="bg-blue-700 hover:bg-blue-800">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset password</DialogTitle>
            <DialogDescription>{pwTarget ? `Imposta una nuova password per ${pwTarget.email}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-slate-700 text-sm">Nuova password</Label>
            <Input data-testid="reset-pw-input" type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5" placeholder="Almeno 6 caratteri" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Annulla</Button>
            <Button data-testid="save-reset-pw-button" onClick={resetPw} className="bg-blue-700 hover:bg-blue-800">Aggiorna</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
